import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { workOrdersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const WorkOrders = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { isAdmin, isAdminOrTis } = useAuth();

  useEffect(() => {
    fetchWorkOrders();
  }, [search, statusFilter]);

  const fetchWorkOrders = async () => {
    try {
      const response = await workOrdersAPI.getAll({ search, status: statusFilter });
      setWorkOrders(response.data.workOrders);
    } catch (error) {
      console.error('Work orders error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu iş emrini silmek istediğinizden emin misiniz?')) return;

    try {
      await workOrdersAPI.delete(id);
      fetchWorkOrders();
    } catch (error) {
      alert(error.response?.data?.error || 'Silme hatası');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: 'Bekliyor',
      in_progress: 'Devam Ediyor',
      completed: 'Tamamlandı',
      cancelled: 'İptal'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getRemainingDaysColor = (days) => {
    if (days < 0) return 'text-red-600 font-bold';
    if (days < 3) return 'text-red-600';
    if (days < 7) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">İş Emirleri</h1>
        {isAdminOrTis() && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Yeni İş Emri
          </button>
        )}
      </div>

      {/* Filtreler */}
      <div className="flex space-x-4">
        <input
          type="text"
          placeholder="İş No veya Firma ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2 border rounded-lg w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tüm Durumlar</option>
          <option value="pending">Bekliyor</option>
          <option value="in_progress">Devam Ediyor</option>
          <option value="completed">Tamamlandı</option>
        </select>
      </div>

      {/* Tablo */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">İş Numarası</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Ana Firma</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Yüklenici</th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Teslim Tarihi</th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Kalan Süre</th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Durum</th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {workOrders.map((order) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <Link to={`/work-orders/${order.id}`} className="font-medium text-blue-600 hover:underline">
                    {order.jobNumber}
                  </Link>
                </td>
                <td className="px-6 py-4 text-gray-800">{order.mainCompany}</td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {order.Assignments?.length > 0 ? (
                      order.Assignments.map((a) => (
                        <span key={a.id} className="px-2 py-0.5 bg-gray-100 rounded text-sm">
                          {a.Company?.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 text-sm">Atanmadı</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-gray-600">
                  {new Date(order.deliveryDate).toLocaleDateString('tr-TR')}
                </td>
                <td className={`px-6 py-4 text-center ${getRemainingDaysColor(order.remainingDays)}`}>
                  {order.remainingDays > 0 ? (
                    <>
                      {Math.floor(order.remainingDays / 30) > 0 && `${Math.floor(order.remainingDays / 30)} ay `}
                      {order.remainingDays % 30} gün
                    </>
                  ) : (
                    'Gecikmiş'
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  {getStatusBadge(order.status)}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex justify-center space-x-2">
                    <Link
                      to={`/work-orders/${order.id}`}
                      className="px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                    >
                      Düzenle
                    </Link>
                    {isAdmin() && (
                      <button
                        onClick={() => handleDelete(order.id)}
                        className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                      >
                        Sil
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {workOrders.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            İş emri bulunmuyor
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <WorkOrderModal
          onClose={() => setShowModal(false)}
          onSave={fetchWorkOrders}
        />
      )}
    </div>
  );
};

// İş Emri Modal
const WorkOrderModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    jobNumber: '',
    mainCompany: '',
    deliveryDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await workOrdersAPI.create(formData);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Yeni İş Emri</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">İş Numarası *</label>
            <input
              type="text"
              value={formData.jobNumber}
              onChange={(e) => setFormData({ ...formData, jobNumber: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: IS-2024-001"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ana Firma *</label>
            <input
              type="text"
              value={formData.mainCompany}
              onChange={(e) => setFormData({ ...formData, mainCompany: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teslim Tarihi *</label>
            <input
              type="date"
              value={formData.deliveryDate}
              onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkOrders;
